import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { PipelineVisualization } from './PipelineVisualization';
import { LightbulbIcon, FlaskConicalIcon, CheckCircle2Icon, RocketIcon, FileTextIcon } from 'lucide-react';
const principles = [{
  icon: LightbulbIcon,
  title: 'Thesis Generation',
  description: 'Ideas flow from research, market observation, and interdisciplinary thinking',
  color: '#CBD5E1'
}, {
  icon: FlaskConicalIcon,
  title: 'Validation Sprints',
  description: 'Rapid prototyping with pre-registered success criteria and data-driven testing',
  color: '#60A5FA'
}, {
  icon: CheckCircle2Icon,
  title: 'Resource Allocation',
  description: 'Annual voting process to advance the most compelling validated ideas',
  color: '#14B8A6'
}, {
  icon: RocketIcon,
  title: 'Execution',
  description: 'Deploy models, build data products, and document learnings with post-mortems',
  color: '#10B981'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 40,
    scale: 0.9
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
export function HowDifferent() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section id="how-different" ref={ref} className="w-full py-32 px-6 bg-navy-900" data-id="element-63">
      <div className="max-w-6xl mx-auto" data-id="element-64">
        <motion.h2 initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-4xl md:text-5xl font-bold mb-12 text-center text-white" data-id="element-65">
          How We're Different
        </motion.h2>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-center mb-16" data-id="element-66">
          <p className="text-xl text-slate mb-4" data-id="element-67">
            <span className="text-[#14B8A6] font-semibold text-2xl" data-id="element-68">
              Quant fund × venture studio.
            </span>
          </p>
          <p className="text-lg text-slate max-w-3xl mx-auto leading-relaxed" data-id="element-69">
            Thesis generation → validation sprints → resource allocation →
            lightweight portfolio management. A repeatable system to convert
            curiosity into shipped prototypes.
          </p>
        </motion.div>
        <motion.div variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20" data-id="element-70">
          {principles.map((principle, index) => {
          const Icon = principle.icon;
          return <motion.div key={principle.title} variants={itemVariants} whileHover={{
            y: -12,
            scale: 1.03,
            transition: {
              duration: 0.3
            }
          }} className="relative group" data-id="element-71">
                <div className="bg-navy-800 border-2 border-navy-700 rounded-lg p-8 h-full hover:border-steel transition-all duration-300 shadow-lg relative overflow-hidden" data-id="element-72">
                  {/* Animated background */}
                  <motion.div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                background: `radial-gradient(circle at 50% 50%, ${principle.color}10, transparent 70%)`
              }} animate={{
                scale: [1, 1.2, 1],
                opacity: [0, 0.3, 0]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-73" />
                  <motion.div initial={{
                scale: 0,
                rotate: -180
              }} animate={inView ? {
                scale: 1,
                rotate: 0
              } : {}} transition={{
                duration: 0.7,
                delay: 0.4 + index * 0.12
              }} className="mb-6 w-16 h-16 rounded-lg flex items-center justify-center relative z-10" style={{
                backgroundColor: `${principle.color}20`
              }} data-id="element-74">
                    <Icon className="w-8 h-8" style={{
                  color: principle.color
                }} data-id="element-75" />
                  </motion.div>
                  <h3 className="text-xl font-bold mb-3 relative z-10" style={{
                color: principle.color
              }} data-id="element-76">
                    {principle.title}
                  </h3>
                  <p className="text-slate leading-relaxed relative z-10" data-id="element-77">
                    {principle.description}
                  </p>
                </div>
                {/* Continuous glow */}
                <motion.div className="absolute inset-0 rounded-lg blur-2xl -z-10" style={{
              background: `radial-gradient(circle at center, ${principle.color}20, transparent 70%)`
            }} animate={{
              opacity: [0.3, 0.6, 0.3],
              scale: [0.95, 1.05, 0.95]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: index * 0.4
            }} data-id="element-78" />
              </motion.div>;
        })}
        </motion.div>
        <PipelineVisualization inView={inView} data-id="element-79" />
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        delay: 1.2,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-20 bg-navy-800 border-2 border-navy-700 rounded-lg p-8 shadow-lg" data-id="element-80">
          <div className="flex items-start gap-4 mb-6" data-id="element-81">
            <FileTextIcon className="w-8 h-8 text-[#14B8A6] flex-shrink-0 mt-1" data-id="element-82" />
            <div data-id="element-83">
              <h3 className="text-2xl font-bold mb-4 text-[#14B8A6]" data-id="element-84">
                Our Philosophy
              </h3>
              <motion.div variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="space-y-4 text-slate" data-id="element-85">
                <motion.p variants={itemVariants} data-id="element-86">
                  <span className="font-semibold text-[#60A5FA]" data-id="element-87">
                    Small by design:
                  </span>{' '}
                  We intentionally limit team size to maintain agility, preserve
                  alpha, and move faster than institutional players.
                </motion.p>
                <motion.p variants={itemVariants} data-id="element-88">
                  <span className="font-semibold text-[#60A5FA]" data-id="element-89">
                    Multidisciplinary:
                  </span>{' '}
                  The best edges emerge at the intersection of fields. We
                  actively recruit from math, CS, philosophy, economics, and
                  beyond.
                </motion.p>
                <motion.p variants={itemVariants} data-id="element-90">
                  <span className="font-semibold text-[#60A5FA]" data-id="element-91">
                    Evidence-based:
                  </span>{' '}
                  Every idea must survive rigorous validation with
                  pre-registered criteria. No hand-waving, no storytelling
                  without data.
                </motion.p>
                <motion.p variants={itemVariants} data-id="element-92">
                  <span className="font-semibold text-[#60A5FA]" data-id="element-93">
                    Learning-focused:
                  </span>{' '}
                  Win or lose, we document everything. Post-mortems are
                  mandatory. Knowledge compounds faster than capital.
                </motion.p>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}